import { Injectable } from '@angular/core';
import { TaskRepository } from 'src/core/tasks/domain/task-repository';
import { TaskService } from 'src/core/tasks/domain/task.service';
import { StartTaskRequest } from './start-task-request';
import { Task } from 'src/core/master-data/domain/task';
import { QueueService } from 'src/core/queue/domain/queue.service';
import { StartTaskQueueElement } from 'src/core/queue/domain/elements/start-task-queue-element';
import { QueueElement, QueueElementError } from 'src/core/queue/domain/queue-element';
import { ConnectionException } from 'src/core/common/domain/exceptions/connection-exception';
import { UnauthorizedException } from 'src/core/authentication/domain/exceptions/unauthorized-exception';

@Injectable({
  providedIn: 'any',
})
export class StartTask {
  private isSavedToServer = false;
  private isStartedToServer = false;
  private isRetry = false;
  private task: Task;

  constructor(
    private readonly taskRepository: TaskRepository,
    private readonly taskService: TaskService,
    private readonly queueService: QueueService
  ) {}

  async execute(request: StartTaskRequest): Promise<void> {
    this.task = request.task;
    const queueElement = request.queueElement;
    this.isRetry = queueElement !== null;
    this.isSavedToServer = queueElement !== null ? queueElement.operationStatus.isSavedToServer : false;
    this.isStartedToServer = queueElement !== null ? queueElement.operationStatus.isStartedToServer : false;

    try {
      if (!this.isRetry) {
        this.task.start();
        await this.saveTaskIntoLocalDevice();
      }
      const taskIsInQueue = this.queueService.hasQueueElementWithTaskId(this.task.id);
      if (taskIsInQueue && !this.isRetry) {
        await this.enqueueOperation();
        return;
      }

      await this.saveTaskToExternalServer(this.task);
      await this.startTaskToExternalServer(this.task);
    } catch (exception) {
      if (exception instanceof ConnectionException) {
        if (this.isRetry) {
          await this.keepQueueElementForNextRetry(queueElement);
          throw exception;
        }

        await this.enqueueOperation();
        return;
      }

      // exceptions during retrying do not enqueue or rollback the task, but store error to display in list
      if (this.isRetry) {
        await this.storeErrorsInQueueElement(exception, queueElement);
        throw exception; // propagate exception without doing anything else
      }

      // otherwise, rollback
      this.task.rollback();
      await this.saveTaskIntoLocalDevice();
      throw exception; // propagate exception
    }
  }

  private async saveTaskIntoLocalDevice() {
    await this.taskService.saveTask(this.task);
  }

  private async saveTaskToExternalServer(task: Task) {
    if (this.isSavedToServer) {
      return;
    }
    await this.taskRepository.save(task);
    this.isSavedToServer = true;
  }

  private async startTaskToExternalServer(task: Task) {
    if (this.isStartedToServer) {
      return;
    }
    await this.taskRepository.start(task);
    this.isStartedToServer = true;
  }

  private async enqueueOperation(errors: Array<QueueElementError> = []) {
    const operationStatus = {
      isSavedToServer: this.isSavedToServer,
      isStartedToServer: this.isStartedToServer,
    };
    await this.queueService.enqueue(StartTaskQueueElement.fromTask(this.task, operationStatus, errors));
  }

  /**
   * A retried element is still in the queue, and returning normally makes the caller drop it as processed,
   * so enqueueing a new one would move the operation behind later ones. Throwing keeps the original in
   * place, with the progress made so far, so the next retry does not repeat what already reached the server.
   */
  private async keepQueueElementForNextRetry(queueElement: StartTaskQueueElement) {
    await this.updateQueueElementWithErrors(queueElement, queueElement.errors);
  }

  private async storeErrorsInQueueElement(exception: unknown, queueElement: StartTaskQueueElement) {
    // an expired session is handled by the session flow, it says nothing about this operation
    if (exception instanceof UnauthorizedException) {
      return;
    }
    await this.updateQueueElementWithErrors(queueElement, QueueElement.errorsFromException(exception));
  }

  private async updateQueueElementWithErrors(queueElement: StartTaskQueueElement, errors: Array<QueueElementError>) {
    const operationStatus = {
      isSavedToServer: this.isSavedToServer,
      isStartedToServer: this.isStartedToServer,
    };
    queueElement.update(this.task, operationStatus, errors);
    await this.queueService.updateQueueElement(queueElement);
  }
}
